import type { IStates } from '../../../interface/IReducer';
import type { ICreatePUResult, ICreatePUResultBase, ICreatePUResultV2, IResult } from '../../../store/actions/query/irev';
import type { IComponentState } from '../../layout/component-state';

/**
 * Pure builders for the polling-unit submission payload.
 *
 * These live outside the hook so the riskiest change in the V2 migration is unit-testable
 * without rendering anything. `usePollingUnitResultDataManager` is a thin wrapper over them.
 */

/** The slice of the upload-flow form state a polling-unit payload is assembled from. */
export type IPuPayloadGlobal = Pick<IComponentState, 'resultSummary' | 'uploadedFiles' | 'electionData' | 'selectedParentCode'>;

export interface IPuPayloadInput {
	states: IStates | undefined;
	global: IPuPayloadGlobal;
	results: IResult[];
	/** Identity of the submitting agent, read from the JWT by the caller. */
	user: { id: string; name: string };
}

/**
 * The submitting agent. `ResultSource.Agent` is **2** — the enum is
 * `Citizen=1, Agent=2, IRev=3, AI=4` and there is no 0, so a 0 here would bind without
 * error and then fail every downstream source comparison silently.
 */
const RESULT_SOURCE_AGENT = 2;

const buildBase = ({ states, global, results, user }: IPuPayloadInput): ICreatePUResultBase => {
	const { resultSummary, uploadedFiles, selectedParentCode } = global;

	return {
		accreditedVoters: resultSummary.DataAccreditedVoters,
		ballotPapersIssuedToPoolingUnit: resultSummary.DataBallotPapersIssuedToPoolingUnit,
		totalUsedBallotPapers: resultSummary.DataTotalUsedBallotPapers,
		totalValidVotes: resultSummary.DataTotalValidVotes,
		resultSource: RESULT_SOURCE_AGENT,
		unusedBallotPapers: resultSummary.DataUnusedBallotPapers,
		votersOnRegister: resultSummary.DataVotersOnRegister,
		rejectedBallot: resultSummary.DataRejectedBallot,
		documentUrls: uploadedFiles,
		results: results.map((i) => ({
			partyId: i.partyId,
			partyName: i.partyName,
			votes: i.votes,
		})),
		createdBy: {
			id: parseInt(user.id || ''),
			name: user.name || '',
		},
		// The server calls .Trim() on this unconditionally, so it must never be null/undefined.
		poolingUnitCode: String(selectedParentCode?.code ?? ''),
		poolingUnitName: String(selectedParentCode?.name ?? ''),
		poolingUnitId: selectedParentCode?.codeId,
		presidingOfficer: {
			id: user.id || '',
			name: user.name || '',
		},
		flags: [],
	};
};

/** V1 payload — unchanged shape, kept so reverting EC8A to v1 is a flag flip. */
export const buildPuResultV1 = (input: IPuPayloadInput): ICreatePUResult => ({
	...buildBase(input),
	election: {
		id: input.global.electionData?.data?.[0]?.electionId || 0,
		name: input.global.electionData?.data?.[0]?.election || '',
	},
});

const geoZoneOf = (states: IStates | undefined) => {
	const zones = states?._geographicalzone?.get_GeographicalZone?.data || [];
	const geoZoneId = states?._countryState?.get_CountryStateByID?.data?.geographicalZoneId || 0;

	return zones.filter((i) => `${i.id}` === `${geoZoneId}`)?.[0];
};

/**
 * V2 payload — the saga entry point.
 *
 * Every field comes from Redux state the app already loads; nothing here needs a new endpoint.
 * `get_PoolingUnitByCode` supplies ward and LGA in one call, `get_CountryStateByID` supplies
 * the state code (the polling-unit response carries only a state id and name, no code), and
 * `get_ElectionByID` supplies the full election entity that V2 requires in place of V1's
 * stripped `{id, name}`.
 */
export const buildPuResultV2 = (input: IPuPayloadInput): ICreatePUResultV2 => {
	const { states, global } = input;
	const { selectedParentCode } = global;

	const election = states?._election?.get_ElectionByID?.data;
	const pu = states?._poolingUnit?.get_PoolingUnitByCode?.data;
	const countryState = states?._countryState?.get_CountryStateByID?.data;
	const geoZone = geoZoneOf(states);

	return {
		...buildBase(input),
		election: {
			id: election?.id ?? 0,
			name: election?.name ?? '',
			electionCycleId: election?.electionCycleId ?? 0,
			constituencyId: election?.constituencyId ?? 0,
			isSpecialElection: election?.isSpecialElection ?? false,
			electionCycle: {
				id: election?.electionCycle?.id ?? 0,
				electionTypeId: election?.electionCycle?.electionTypeId ?? 0,
				name: election?.electionCycle?.name ?? '',
				year: election?.electionCycle?.year ?? '',
				startDate: election?.electionCycle?.startDate ?? '',
				endDate: election?.electionCycle?.endDate ?? '',
			},
			constituency: {
				id: election?.constituency?.id ?? 0,
				name: election?.constituency?.name ?? '',
				// The core read returns these two as null; the V2 schema marks them nullable, so
				// they are coalesced for type shape only, not to satisfy a server requirement.
				code: election?.constituency?.code ?? '',
				alias: election?.constituency?.alias ?? '',
				stateId: election?.constituency?.stateId ?? 0,
				electiveCategoryId: election?.constituency?.electiveCategoryId ?? 0,
				isCouncilorship: election?.constituency?.isCouncilorship ?? false,
				lgaCount: election?.constituency?.lgaCount ?? 0,
				wardCount: election?.constituency?.wardCount ?? 0,
				pollingUnitCount: election?.constituency?.pollingUnitCount ?? 0,
			},
		},
		pollingUnit: {
			code: String(selectedParentCode?.code ?? ''),
			name: String(selectedParentCode?.name ?? ''),
		},
		ward: {
			code: pu?.ward?.wardCode ?? '',
			name: pu?.ward?.name ?? '',
		},
		localGovernment: {
			code: pu?.lga?.lgaCode ?? '',
			name: pu?.lga?.name ?? '',
		},
		state: {
			code: countryState?.stateCode ?? '',
			name: countryState?.name ?? '',
		},
		geoZone: {
			code: geoZone?.code ?? '',
			name: geoZone?.name ?? '',
		},
		zone: {
			code: geoZone?.code ?? '',
			name: geoZone?.zone?.name ?? '',
		},
	};
};

/** Agent-facing labels for the parts of the payload that come from a background lookup. */
const V2_FIELD_LABELS: Record<string, string> = {
	election: 'election details',
	pollingUnit: 'polling unit',
	ward: 'ward',
	localGovernment: 'local government',
	state: 'state',
	geoZone: 'geopolitical zone',
	zone: 'zone',
};

/**
 * Which required parts of a V2 payload are still empty.
 *
 * This exists because the server cannot catch this: the six division objects are `[REQUIRED]`
 * but their inner `code`/`name` are nullable, so `{ code: '', name: '' }` is accepted and
 * creates a saga row with a blank code. A blank row is worse than a rejected submission — it
 * looks successful and corrupts every comparison built on top of it.
 *
 * Returns agent-facing labels, so the caller can name what is missing rather than saying
 * "something went wrong".
 */
export const missingV2Fields = (payload: ICreatePUResultV2): string[] => {
	const missing: string[] = [];

	if (!payload.election?.id) missing.push(V2_FIELD_LABELS.election);

	for (const key of ['pollingUnit', 'ward', 'localGovernment', 'state', 'geoZone', 'zone'] as const) {
		const division = payload[key];
		if (!division?.code?.trim() || !division?.name?.trim()) missing.push(V2_FIELD_LABELS[key]);
	}

	// Guarded separately: the server .Trim()s this without a null check, so an empty value is
	// both a data problem and a crash risk.
	if (!payload.poolingUnitCode?.trim()) missing.push('polling unit code');

	return missing;
};
